import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: API_URL,
});

// Helper function to convert photo paths to full URLs
export const getPhotoUrl = (photoPath) => {
  if (!photoPath) return null;
  
  // If it's already a full URL (http/https), return as-is
  if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
    return photoPath;
  }
  
  // Otherwise, prepend the API URL and files endpoint
  return `${API_URL}/api/files/${photoPath}`;
};

// Fallback image for listings
export const FALLBACK_LISTING_IMAGE = 'https://images.unsplash.com/photo-1502700807168-484a3e7889d0?w=800';

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Listings API
export const listingsApi = {
  getAll: (params) => api.get('/api/listings', { params }),
  getById: (id, token) => api.get(`/api/listings/${id}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }),
  create: (data, token) => api.post('/api/listings', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  update: (id, data, token) => api.put(`/api/listings/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  toggleStatus: (id, token) => api.put(`/api/listings/${id}/toggle-status`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  reactivate: (id, data, token) => api.put(`/api/listings/${id}/reactivate`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getHostListings: (token) => api.get('/api/host/listings', {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// Bookings API
export const bookingsApi = {
  getAll: (token) => api.get('/api/bookings', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getById: (id, token) => api.get(`/api/bookings/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  create: (data, token) => api.post('/api/bookings', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  updateStatus: (id, status, token) => api.put(`/api/bookings/${id}/status`, { status }, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  cancel: (id, reason, token) => api.post(`/api/bookings/${id}/cancel`, { booking_id: id, reason }, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  markNoShow: (id, noShowType, token) => api.post(`/api/bookings/${id}/no-show?no_show_type=${noShowType}`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getCancellationPreview: (id, token) => api.get(`/api/bookings/${id}/cancellation-preview`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getMyCancellations: (token) => api.get('/api/my-cancellations', {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// Messages API
export const messagesApi = {
  getByBooking: (bookingId, token) => api.get(`/api/messages/${bookingId}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  create: (data, token) => api.post('/api/messages', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// User Account API
export const userApi = {
  changePassword: (data, token) => api.post('/api/auth/change-password', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// Reviews API
export const reviewsApi = {
  getByListing: (listingId) => api.get(`/api/reviews/listing/${listingId}`),
  create: (data, token) => api.post('/api/reviews', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  canReview: (bookingId, token) => api.get(`/api/reviews/can-review/${bookingId}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// Upload API
export const uploadApi = {
  upload: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`
      }
    });
  },
};

// Admin API
export const adminApi = {
  getAllListings: (token) => api.get('/api/admin/listings', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  approveListing: (id, token) => api.put(`/api/admin/listings/${id}/approve`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  rejectListing: (id, token) => api.put(`/api/admin/listings/${id}/reject`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  removeListing: (id, token) => api.delete(`/api/admin/listings/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getAllUsers: (token) => api.get('/api/admin/users', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  suspendUser: (id, token) => api.put(`/api/admin/users/${id}/suspend`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  activateUser: (id, token) => api.put(`/api/admin/users/${id}/activate`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getAllBookings: (token) => api.get('/api/admin/bookings', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getStats: (token) => api.get('/api/admin/stats', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getVerifications: (token) => api.get('/api/admin/verifications', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  approveVerification: (id, token) => api.put(`/api/admin/verifications/${id}/approve`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  rejectVerification: (id, token) => api.put(`/api/admin/verifications/${id}/reject`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  // Fee configuration endpoints
  getFeeConfig: (token) => api.get('/api/admin/fee-config', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  updateFeeConfig: (data, token) => api.put('/api/admin/fee-config', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  resetFeeConfig: (token) => api.post('/api/admin/fee-config/reset', {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  previewFeeCalculation: (price, token) => api.post(`/api/admin/fee-config/calculate?listing_price=${price}`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getPlatformRevenue: (token) => api.get('/api/admin/platform-revenue', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getPendingPayouts: (token) => api.get('/api/admin/payouts', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  // Category management endpoints
  getCategories: (token) => api.get('/api/admin/categories', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  createCategory: (data, token) => api.post('/api/admin/categories', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  updateCategory: (id, data, token) => api.put(`/api/admin/categories/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  deleteCategory: (id, token) => api.delete(`/api/admin/categories/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  seedCategories: (token) => api.post('/api/admin/categories/seed', {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  // Cancellation monitoring endpoints
  getCancellations: (userType, flaggedOnly, token) => api.get(`/api/admin/cancellations?${userType ? `user_type=${userType}&` : ''}${flaggedOnly ? 'flagged_only=true' : ''}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getCancellationStats: (userType, flaggedOnly, token) => api.get(`/api/admin/cancellation-stats?${userType ? `user_type=${userType}&` : ''}${flaggedOnly ? 'flagged_only=true' : ''}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getCancellationDetail: (id, token) => api.get(`/api/admin/cancellations/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  removeUserFlag: (userId, token) => api.put(`/api/admin/users/${userId}/remove-flag`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  addUserNotes: (userId, notes, token) => api.post(`/api/admin/users/${userId}/notes?notes=${encodeURIComponent(notes)}`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// Categories API (Public)
export const categoriesApi = {
  getAll: () => api.get('/api/categories'),
  getBySlug: (slug) => api.get(`/api/categories/${slug}`),
};

// Cancellation Policy API
export const cancellationApi = {
  getPolicy: () => api.get('/api/cancellation-policy'),
};

// Host API
export const hostApi = {
  getStats: (token) => api.get('/api/host/stats', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getConversations: (token) => api.get('/api/host/conversations', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  markMessagesRead: (bookingId, token) => api.put(`/api/messages/${bookingId}/read`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  // Stripe Connect for Host Payouts
  createConnectAccount: (token, country = 'US') => api.post('/api/host/connect/create-account', { country }, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getOnboardingLink: (token) => api.post('/api/host/connect/onboarding-link', {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getConnectStatus: (token) => api.get('/api/host/connect/status', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getEarnings: (token) => api.get('/api/host/earnings', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  requestPayout: (token, amount = null) => api.post('/api/host/connect/request-payout', { amount }, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getDashboardLink: (token) => api.get('/api/host/connect/dashboard-link', {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// Disputes API
export const disputesApi = {
  create: (data, token) => api.post('/api/disputes', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getGuestDisputes: (token) => api.get('/api/disputes/guest', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getHostDisputes: (token) => api.get('/api/disputes/host', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  checkEligibility: (bookingId, token) => api.get(`/api/bookings/${bookingId}/can-dispute`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  // Admin
  getAll: (token, status = null) => api.get('/api/admin/disputes', {
    params: status ? { status } : {},
    headers: { Authorization: `Bearer ${token}` }
  }),
  resolve: (disputeId, data, token) => api.post(`/api/admin/disputes/${disputeId}/resolve`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};